import type { TablePaginationConfig } from 'antd';
import type { Dayjs } from 'dayjs';
import { z } from 'zod';
import type { TotalizadorListData } from '@/components/totalizador/TotalizadorList';
import type { AcaoTipo } from '@/features/acao/types/AcaoTipo';
import type { ClienteFiscalPerfil } from '@/features/cliente/types/enum';
import type { FormaPagamento } from '@/features/forma-pagamento/types/FormaPagamento';
import type { Marcador } from '@/features/marcador/types/Marcador';
import type { NotaFiscal } from '@/features/nfe/types/NotaFiscal';
import type { Produto } from '@/features/produto/types/Produto';
import type { ApiDispositivoSistemasEmUso } from '@/features/sistema-integrado/api';
import type { ReduxFn } from '@/std/redux';
import type { BasicSelectOption, SimNao } from '@/types';
import type { ValidateErrorEntity } from '@/types/antd';
import type { FormAction } from '@/types/FormAction';
import type { SortParams } from '@/types/interfaces';
import { VendaTipoSchema } from '@/features/venda/types/VendaTipo';

type TCalcularImpostosApiItens = {
    nfi_regra_fiscal_idpk: number;
    nfi_valor_unitario: number;
    nfi_quantidade: number;
    nfi_ncm: string;
    nfi_beneficio_fiscal_codigo: string;
    info?: {
        regra_fiscal_ipi_idpk: number;
        regra_fiscal_icms_idpk: number;
        regra_fiscal_pis_idpk: number;
        regra_fiscal_cofins_idpk: number;
        regra_fiscal_ii_idpk: number;
        regra_fiscal_informacoes_idpk: number;
    };
    nfi_ipi_cst: string;
    nfi_ipi_cpf_cnpj_produtor: string;
    nfi_ipi_extipi: string;
    nfi_ipi_somar_bc_icms: SimNao;
    nfi_ipi_base: number;
    nfi_ipi_aliquota: number;
    nfi_ipi_valor: number;
    nfi_icmsefetivo_base: number;
    nfi_icmsefetivo_aliquota: number;
    nfi_icmsefetivo_valor: number;
    nfi_icmsefetivo_reducao: number;
    nfi_icmsstret_aliquota: number;
    nfi_icmsstret_base: number;
    nfi_icmsstret_valor: number;
    nfi_icmsstret_substituto: number;
    nfi_icmsstret_fcp_base: number;
    nfi_icmsstret_fcp_aliquota: number;
    nfi_icmsstret_fcp_valor: number;
    nfi_cfop_codigo: number;
    nfi_simples_csosn: string;
    nfi_icms_cst: string;
    nfi_icms_aliquota: number;
    nfi_icms_modalidade: number;
    nfi_icms_diferido_percentual: number;
    nfi_icms_diferido_valor: number;
    nfi_icmsop_diferido_valor: number;
    nfi_icms_reducao: number;
    nfi_icms_base: number;
    nfi_icms_valor: number;
    nfi_icmsst_reducao: number;
    nfi_icmsst_base: number;
    nfi_icmsst_valor: number;
    nfi_icmsst_mva: number;
    nfi_pis_cst: string;
    nfi_pis_base: number;
    nfi_pis_aliquota: number;
    nfi_pis_valor: number;
    nfi_pisst_base: number;
    nfi_pisst_aliquota: number;
    nfi_pisst_valor: number;
    nfi_cofins_cst: string;
    nfi_cofins_base: number;
    nfi_cofins_aliquota: number;
    nfi_cofins_valor: number;
    nfi_cofinsst_base: number;
    nfi_cofinsst_aliquota: number;
    nfi_cofinsst_valor: number;
    nfi_ii_iof: number;
    nfi_ii_despesas_aduaneiras: number;
    nfi_informacoes_adicionais: string;
    nfi_ibpt_total_nacional: number;
    nfi_ibpt_total_estadual: number;
    nfi_ibpt_total_municipal: number;
};

export type TCalcularImpostosApi = {
    itens: TCalcularImpostosApiItens[];
    nof_dest_consumidorfinal: 0 | 1;
    nof_dest_crt: number;
    nof_dest_indicadorie: number;
    nof_dest_pais_codigo: number;
    nof_dest_uf: string;
    nof_ibpt_chave: string;
    nof_ibpt_fonte: string;
    nof_motivo_venda?: number;
    nof_motivo_compra?: number;
    nof_movimento: number;
    nof_tipo: number;
    nof_transp_somar_total: number;
};

export type TCalcImpostosItens = {
    nfi_produto_idpk: number;
    nfi_regra_fiscal_idpk: number;
    nfi_valor_unitario: number;
    nfi_quantidade: number;
    nfi_ncm: string;
    nfi_beneficio_fiscal_codigo: string;
    nfi_desconto_tipo: string;
    nfi_desconto_valor: number;
    nfi_valor_outro: number;
    nfi_valor_frete: number;
    nfi_valor_seguro: number;
    nfi_icmsstret_aliquota?: number;
    nfi_icmsstret_base?: number;
    nfi_icmsstret_valor?: number;
    nfi_icmsstret_substituto?: number;
    produto?: {
        pro_icmsstret_base?: number;
        pro_icmsstret_aliquota?: number;
        pro_icmsstret_valor?: number;
        pro_icmsstret_substituto?: number;
        pro_icmsstret_fcp_aliquota?: number;
    };
};

export type TCalcImpostos = {
    nof_tipo?: number | null;
    nof_motivo_venda?: number | null;
    nof_motivo_compra?: number | null;
    nof_remessa_retorno_motivo?: number | null;
    nof_movimento?: number | null;
    nof_dest_uf?: string | null;
    nof_dest_pais_codigo?: number | null;
    nof_dest_consumidorfinal?: 0 | 1 | null;
    nof_dest_indicadorie?: number | null;
    nof_dest_crt?: number | null;
    nof_dest_perfil?: ClienteFiscalPerfil | null;
    nof_transp_somar_total?: number | null;
    itens: TCalcImpostosItens[];
};

export type TValorTotaisNf = {
    tipo: string;
    valor_total: number;
    quantidade_total: number;
};

export type TGetTable = {
    // FILTROS
    filterDateRange?: Interval<Dayjs>;
    filterPesquisar?: string;
    filterTipo?: BasicSelectOption;
    filterCliente?: BasicSelectOption;
    filterFornecedor?: BasicSelectOption;
    filterVendedor?: BasicSelectOption;
    filterStatus?: string[];
    filterSerie?: number;
    filterNumero?: number;
    filterCidade?: BasicSelectOption;
    filterMarcador?: Marcador[];
    filterVendaExterna?: BasicSelectOption;
    filterProdutos?: Produto[];
    filterAcao?: AcaoTipo[];
    filterSistemasIntegrados?: ApiDispositivoSistemasEmUso[];
    filterFormasPagamento?: FormaPagamento[];

    // TOTAIS REGISTROS (PARA PAGINAÇÃO)
    totalRegistrosTable: number;
    registroInitial: number;
    qtdRegistros: number;
    resetPagination?: boolean;

    // ORDENAÇÃO
    sortParams: SortParams;

    // PAGINAÇÃO
    pagination: TablePaginationConfig;

    // UPDATE TABLE ON CHANGES
    updateTable: boolean;

    // LOADING NA TABELA PARA MOSTRAR ALGUMA AÇÃO EXECUTANDO
    loadingTable: boolean;
};

export const NotaFiscalProntaEntregaTipoSchema = VendaTipoSchema.or(z.literal('devolução'));

export type NotaFiscalProntaEntregaTipo = z.infer<typeof NotaFiscalProntaEntregaTipoSchema>;

export type NotaFiscalState = {
    // EFFECTS
    get: ReduxFn<NotaFiscal[]>;
    getOne: ReduxFn<NotaFiscal>;
    post: ReduxFn<{ nof_idpk: number }>;
    put: ReduxFn<string>;
    remove: ReduxFn<string>;
    totalizador: ReduxFn<TotalizadorListData[]>;
    cancelarNotaFiscal: ReduxFn<undefined>;
    preVisualizarDanfe: ReduxFn<undefined>;
    gerarRelatorio: ReduxFn<undefined>;
    gerarRelatorioMultiple: ReduxFn<undefined>;
    compartilharSend: ReduxFn<undefined>;
    calcularImpostos: ReduxFn<TCalcularImpostosApi>;
    getReceitaFiscal: ReduxFn<string>;
    lancarEstoque: ReduxFn<undefined>;
    estornarEstoque: ReduxFn<undefined>;
    lancarFinanceiro: ReduxFn<undefined>;
    estornarFinanceiro: ReduxFn<undefined>;
    enviarExpedicao: ReduxFn<undefined>;
    verifyAndGerarRelatorioPdf: ReduxFn<undefined>;

    // TABELAS
    getTable: TGetTable;

    // MOSTRAR DRAWERS
    showDrawerCancelarNf: boolean;

    // FORM
    formAction?: FormAction;
    formError?: ValidateErrorEntity;
    triggerFormReload?: string;

    // ref notas de vendas externas (pronta entrega)
    isNotaProntaEntrega?: boolean;
    notaProntaEntregaTipo?: NotaFiscalProntaEntregaTipo;
};
